import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Switch from '@radix-ui/react-switch';
import * as Slider from '@radix-ui/react-slider';
import * as Select from '@radix-ui/react-select';
import { Accessibility, X, ChevronDown, Check } from 'lucide-react';
import { useAccessibilityStore } from '../store/accessibilityStore';

const colorOptions = [
  { value: 'blue', label: 'Blue' },
  { value: 'red', label: 'Red' },
  { value: 'brown', label: 'Brown' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'lightblue', label: 'Light Blue' },
  { value: 'green', label: 'Green' },
  { value: 'black', label: 'Black' }
];

const ColorSelect = ({ 
  label, 
  value, 
  onChange 
}: { 
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const selectedColor = colorOptions.find(color => color.value === value) || colorOptions[0];
  
  return (
    <div className="space-y-2">
      <label className="font-medium">{label}</label>
      <Select.Root value={value} onValueChange={onChange}>
        <Select.Trigger className="inline-flex items-center justify-between w-full px-4 py-2 bg-white border rounded-md">
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: selectedColor.value }}
            />
            <Select.Value>{selectedColor.label}</Select.Value>
          </div>
          <Select.Icon>
            <ChevronDown className="h-4 w-4" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content 
            className="bg-white border rounded-md shadow-lg min-w-[180px] z-50"
            position="popper"
            sideOffset={5}
          >
            <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white text-gray-700 cursor-default">
              <ChevronDown className="h-4 w-4 rotate-180" />
            </Select.ScrollUpButton>
            <Select.Viewport>
              {colorOptions.map((color) => (
                <Select.Item
                  key={color.value}
                  value={color.value}
                  className="flex items-center px-4 py-2 outline-none hover:bg-gray-100 cursor-pointer relative select-none"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: color.value }}
                    />
                    <Select.ItemText>{color.label}</Select.ItemText>
                  </div>
                  <Select.ItemIndicator className="absolute right-4">
                    <Check className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white text-gray-700 cursor-default">
              <ChevronDown className="h-4 w-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};

export function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const store = useAccessibilityStore();

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition z-50"
        aria-label="Open Accessibility Menu"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg w-[90vw] max-w-2xl max-h-[85vh] overflow-y-auto z-50">
            <div className="flex justify-between items-center mb-6">
              <Dialog.Title className="text-2xl font-bold">
                Accessibility Options
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-6">
              {/* Navigation and Screen Reader */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Adapt to keyboard navigation</label>
                  <Switch.Root
                    checked={store.keyboardNavigation}
                    onCheckedChange={store.setKeyboardNavigation}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Adapt to screen reader</label>
                  <Switch.Root
                    checked={store.screenReader}
                    onCheckedChange={store.setScreenReader}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Block animations</label>
                  <Switch.Root
                    checked={store.blockAnimations}
                    onCheckedChange={store.setBlockAnimations}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>
              </div>

              {/* Font Size */}
              <div className="space-y-2">
                <label className="font-medium">Text Size</label>
                <Slider.Root
                  className="relative flex items-center w-full h-5"
                  value={[store.fontSize]}
                  onValueChange={([value]) => store.setFontSize(value)}
                  max={24}
                  min={12}
                  step={1}
                >
                  <Slider.Track className="bg-gray-200 relative grow h-1 rounded-full">
                    <Slider.Range className="absolute bg-blue-600 h-full rounded-full" />
                  </Slider.Track>
                  <Slider.Thumb
                    className="block w-5 h-5 bg-blue-600 rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Font size"
                  />
                </Slider.Root>
              </div>

              {/* Line Spacing */}
              <div className="space-y-2">
                <label className="font-medium">Line Spacing</label>
                <Slider.Root
                  className="relative flex items-center w-full h-5"
                  value={[store.lineSpacing]}
                  onValueChange={([value]) => store.setLineSpacing(value)}
                  max={2.5}
                  min={1}
                  step={0.1}
                >
                  <Slider.Track className="bg-gray-200 relative grow h-1 rounded-full">
                    <Slider.Range className="absolute bg-blue-600 h-full rounded-full" />
                  </Slider.Track>
                  <Slider.Thumb
                    className="block w-5 h-5 bg-blue-600 rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Line spacing"
                  />
                </Slider.Root>
              </div>

              {/* Color Settings */}
              <div className="space-y-4">
                <ColorSelect
                  label="Text Color"
                  value={store.textColor}
                  onChange={store.setTextColor}
                />
                <ColorSelect
                  label="Background Color"
                  value={store.backgroundColor}
                  onChange={store.setBackgroundColor}
                />
                <ColorSelect
                  label="Title Color"
                  value={store.titleColor}
                  onChange={store.setTitleColor}
                />
                <ColorSelect
                  label="Cursor Color"
                  value={store.cursorColor}
                  onChange={store.setCursorColor}
                />
              </div>

              {/* Display Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Readable font</label>
                  <Switch.Root
                    checked={store.readableFont}
                    onCheckedChange={store.setReadableFont}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Reading aid</label>
                  <Switch.Root
                    checked={store.readingAid}
                    onCheckedChange={store.setReadingAid}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Reading mode</label>
                  <Switch.Root
                    checked={store.readingMode}
                    onCheckedChange={store.setReadingMode}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Reading mask</label>
                  <Switch.Root
                    checked={store.readingMask}
                    onCheckedChange={store.setReadingMask}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>
              </div>

              {/* Contrast Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Reverse contrast</label>
                  <Switch.Root
                    checked={store.reverseContrast}
                    onCheckedChange={store.setReverseContrast}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Bright contrast</label>
                  <Switch.Root
                    checked={store.brightContrast}
                    onCheckedChange={store.setBrightContrast}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Colorless display</label>
                  <Switch.Root
                    checked={store.colorlessDisplay}
                    onCheckedChange={store.setColorlessDisplay}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Dark contrast</label>
                  <Switch.Root
                    checked={store.darkContrast}
                    onCheckedChange={store.setDarkContrast}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">High saturation</label>
                  <Switch.Root
                    checked={store.highSaturation}
                    onCheckedChange={store.setHighSaturation}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>
              </div>

              {/* Additional Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Big Cursor</label>
                  <Switch.Root
                    checked={store.bigCursor}
                    onCheckedChange={store.setBigCursor}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Highlight links</label>
                  <Switch.Root
                    checked={store.highlightLinks}
                    onCheckedChange={store.setHighlightLinks}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Highlight titles</label>
                  <Switch.Root
                    checked={store.highlightTitles}
                    onCheckedChange={store.setHighlightTitles}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium">Show image descriptions</label>
                  <Switch.Root
                    checked={store.showImageDescriptions}
                    onCheckedChange={store.setShowImageDescriptions}
                    className="w-11 h-6 bg-gray-200 rounded-full relative data-[state=checked]:bg-blue-600"
                  >
                    <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
                  </Switch.Root>
                </div>
              </div>

              {/* Reset Button */}
              <button
                onClick={store.resetSettings}
                className="w-full bg-red-600 text-white py-2 rounded-md hover:bg-red-700 transition"
              >
                Reset Settings
              </button>

              {/* Accessibility Statement */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Accessibility Statement</h3>
                <p className="text-sm text-gray-600">
                  Israeli law places emphasis and importance on the accessibility of its websites, equality, non-discrimination and accessible service for people with disabilities. This website was made accessible by the website accessibility company "Equally AI".
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  The website complies with the requirements of the Equal Rights for People with Disabilities Regulations (Accessibility Adjustments to Service), 2013.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  The accessibility adjustments were made according to the recommendations of the Israeli standard (TI 5568) for the accessibility of Internet content at level AA and the international WCAG2.0 document.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  The website provides a semantic structure for assistive technologies and supports the standard usage pattern for operation with a keyboard using the arrow keys, Enter and Esc to exit menus and windows.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  In order to obtain the best browsing experience with screen reading software, we recommend using the latest NVDA software.
                </p>
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Contact the accessibility coordinator:</h4>
                  <p className="text-sm text-gray-600">
                    Name: Yehuda<br />
                    Position: Accessibility Manager<br />
                    Telephone: 054-5665473<br />
                    Email: service@israeli.law
                  </p>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}